import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import type { StatutoryCatalogueTemplateView, StatutoryLeaveTemplateInput } from '../../../../models/attendance';
import { StatutoryFacts, StatutoryValueFields } from '../../../Attendance/StatutoryLeaveTemplates/statutoryFields';
import {
  readStatutoryDraft, toStatutoryDraft, emptyStatutoryDraft, type StatutoryDraft, type StatutoryDraftErrors,
} from '../../../Attendance/StatutoryLeaveTemplates/statutoryDraft';

export interface CountryOption { value: string; label: string }

interface EditorProps {
  idPrefix: string;
  heading: string;
  initial?: StatutoryCatalogueTemplateView;
  countryOptions: CountryOption[];
  defaultCountryId?: number | null;
  /** Moving a row to another country is refused once companies hold copies of it. */
  countryLocked: boolean;
  note: string;
  submitLabel: string;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (input: StatutoryLeaveTemplateInput) => Promise<boolean>;
}

/** The inline create / edit form for one catalogue row. */
export function CatalogueEditor({
  idPrefix, heading, initial, countryOptions, defaultCountryId, countryLocked, note, submitLabel, saving, onCancel, onSubmit,
}: EditorProps) {
  const { t } = useTranslation(['leaves', 'common']);
  const [draft, setDraft] = useState<StatutoryDraft>(() => (initial ? toStatutoryDraft(initial) : { ...emptyStatutoryDraft }));
  const [countryId, setCountryId] = useState<string>(() => {
    const id = initial?.countryId ?? defaultCountryId;
    return id ? String(id) : '';
  });
  const [errors, setErrors] = useState<StatutoryDraftErrors>({});
  const [countryError, setCountryError] = useState<string | undefined>();

  const submit = async () => {
    const { values, errors: found } = readStatutoryDraft(draft, t);
    setErrors(found);
    const missingCountry = !countryId;
    setCountryError(missingCountry ? t('leaves:statutoryTemplates.errorCountryRequired') : undefined);
    if (!values || missingCountry) return;
    await onSubmit({ ...values, countryId: Number(countryId) });
  };

  return (
    <div className="flex flex-col gap-4" data-testid={`${idPrefix}-editor`}>
      <h3 className="text-sm font-semibold text-foreground">{heading}</h3>
      <div className="sm:max-w-sm">
        <Select
          id={`${idPrefix}-country`}
          label={t('leaves:statutoryTemplates.country')}
          options={countryOptions}
          value={countryId}
          onChange={(v: string | number) => setCountryId(v ? String(v) : '')}
          placeholder={t('leaves:statutoryTemplates.selectCountry')}
          searchable
          disabled={saving || countryLocked}
          description={countryLocked ? t('leaves:statutoryTemplates.countryLocked') : undefined}
          error={countryError}
          data-testid={`${idPrefix}-country`}
        />
      </div>
      <StatutoryValueFields draft={draft} errors={errors} onChange={setDraft} idPrefix={idPrefix} disabled={saving} />
      <p className="max-w-prose text-xs text-muted-foreground">{note}</p>
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={saving} data-testid={`${idPrefix}-cancel`}>
          {t('common:Cancel')}
        </Button>
        <Button onClick={submit} loading={saving} disabled={saving} data-testid={`${idPrefix}-submit`}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

interface RowProps {
  row: StatutoryCatalogueTemplateView;
  countryOptions: CountryOption[];
  editing: boolean;
  busy: string | null;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (input: StatutoryLeaveTemplateInput) => Promise<boolean>;
  onArchive: () => Promise<boolean>;
  onRestore: () => void;
}

/** One catalogue row: its values, how many companies hold a copy, and its actions. */
export function CatalogueRow({
  row, countryOptions, editing, busy, onEdit, onCancelEdit, onSave, onArchive, onRestore,
}: RowProps) {
  const { t } = useTranslation(['leaves', 'common']);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const rowId = `statutory-catalogue-${row.id}`;

  if (editing) {
    return (
      <li className="bg-muted/40 px-4 py-5 sm:px-5" data-testid={`${rowId}-edit`}>
        <CatalogueEditor
          idPrefix={rowId}
          heading={t('leaves:statutoryTemplates.editing', { name: row.leaveTypeName })}
          initial={row}
          countryOptions={countryOptions}
          countryLocked={row.companyCopyCount > 0}
          note={t('leaves:statutoryTemplates.catalogueEditNote')}
          submitLabel={t('leaves:statutoryTemplates.saveChanges')}
          saving={busy === `save-${row.id}`}
          onCancel={onCancelEdit}
          onSubmit={onSave}
        />
      </li>
    );
  }

  return (
    <li className="px-4 py-4 sm:px-5" data-testid={rowId}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <h3 className="text-base font-semibold text-foreground" data-testid={`${rowId}-name`}>{row.leaveTypeName}</h3>
          <StatutoryFacts values={row} testIdPrefix={rowId} />
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <div className="flex gap-1.5">
              <dt className="text-muted-foreground">{t('leaves:statutoryTemplates.companyCopies')}</dt>
              <dd className="font-medium tabular-nums text-foreground" data-testid={`${rowId}-copies`}>{row.companyCopyCount}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="text-muted-foreground">{t('leaves:statutoryTemplates.customizedCopies')}</dt>
              <dd className="font-medium tabular-nums text-foreground" data-testid={`${rowId}-customized`}>{row.customizedCopyCount}</dd>
            </div>
          </dl>
          {row.notes && <p className="max-w-prose text-sm text-muted-foreground">{row.notes}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 md:justify-end">
          {row.isArchived ? (
            <Button
              variant="outline"
              size="sm"
              iconName="ArchiveRestore"
              onClick={onRestore}
              loading={busy === `restore-${row.id}`}
              disabled={busy != null}
              data-testid={`${rowId}-restore`}
            >
              {t('common:Restore')}
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" iconName="Pencil" onClick={onEdit} disabled={busy != null} data-testid={`${rowId}-edit-btn`}>
                {t('common:Edit')}
              </Button>
              {!confirmingArchive && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Archive"
                  onClick={() => setConfirmingArchive(true)}
                  disabled={busy != null}
                  data-testid={`${rowId}-archive`}
                >
                  {t('common:Archive')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {confirmingArchive && !row.isArchived && (
        <div
          className="mt-3 flex flex-col gap-3 rounded-md border border-border bg-muted/50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
          role="group"
          aria-label={t('common:Archive')}
          data-testid={`${rowId}-archive-confirm`}
        >
          <p className="text-sm text-foreground">{t('leaves:statutoryTemplates.catalogueArchiveConfirm')}</p>
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmingArchive(false)} disabled={busy != null}>
              {t('common:Cancel')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              loading={busy === `archive-${row.id}`}
              disabled={busy != null}
              onClick={async () => { if (await onArchive()) setConfirmingArchive(false); }}
              data-testid={`${rowId}-archive-submit`}
            >
              {t('leaves:statutoryTemplates.catalogueArchiveSubmit')}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
